<script setup lang="ts">
import type { ChatStatus, UIMessage } from 'ai'

import { createParse } from 'comark'
import emoji from 'comark/plugins/emoji'
import { ComarkRenderer } from '@comark/vue'

import iconDocueAi from '~/assets/images/icon-docue-ai.svg?url'

import ChatComponentA from './Component/ChatComponentA.vue'
import ChatComponentH1 from './Component/ChatComponentH1.vue'
import ChatComponentH2 from './Component/ChatComponentH2.vue'
import ChatComponentH3 from './Component/ChatComponentH3.vue'
import ChatComponentH4 from './Component/ChatComponentH4.vue'
import ChatComponentH5 from './Component/ChatComponentH5.vue'
import ChatComponentH6 from './Component/ChatComponentH6.vue'
import ChatComponentLi from './Component/ChatComponentLi.vue'
import ChatComponentOl from './Component/ChatComponentOl.vue'
import ChatComponentUl from './Component/ChatComponentUl.vue'
import ChatComponentHr from './Component/ChatComponentHr.vue'
import ChatComponentEm from './Component/ChatComponentEm.vue'
import ChatComponentPre from './Component/ChatComponentPre.vue'
import ChatComponentCode from './Component/ChatComponentCode.vue'
import ChatComponentTable from './Component/ChatComponentTable.vue'
import ChatComponentStrong from './Component/ChatComponentStrong.vue'
import ChatComponentBlockquote from './Component/ChatComponentBlockquote.vue'

const props = defineProps<{
  message: UIMessage
  isLastMessage: boolean
  status: ChatStatus | undefined
  chatId?: string
}>()

const emit = defineEmits<{
  messageReady: []
}>()

const { notifyBugsnag } = useReportError()

const texts = computed(() => props.message.parts.filter(part => part.type === 'text').map(part => part.text).join('\n\r'))
const parseMarkdown = createParse({ html: false, plugins: [emoji()] })

const componentMap = {
  a: ChatComponentA,
  h1: ChatComponentH1,
  h2: ChatComponentH2,
  h3: ChatComponentH3,
  h4: ChatComponentH4,
  h5: ChatComponentH5,
  h6: ChatComponentH6,
  li: ChatComponentLi,
  ol: ChatComponentOl,
  ul: ChatComponentUl,
  table: ChatComponentTable,
  pre: ChatComponentPre,
  code: ChatComponentCode,
  blockquote: ChatComponentBlockquote,
  hr: ChatComponentHr,
  strong: ChatComponentStrong,
  em: ChatComponentEm,
}

const { data: tree, error } = await useAsyncData(
  `chat-assistant-message-ast-data-${props.message.id}`,
  async () => {
    if (!texts.value)
      return undefined

    return parseMarkdown(texts.value)
  },
  { watch: [texts], deep: false },
)

watch(error, (error) => {
  if (error) {
    console.error(error)
    notifyBugsnag(error)
  }
})

const isReady = computed(() => !!tree.value?.nodes.length && ((props.isLastMessage && props.status === 'ready') || !props.isLastMessage))

whenever(isReady, () => {
  if (!props.isLastMessage)
    return

  emit('messageReady')
})
</script>

<template>
  <article
    v-show="isReady"
    class="w-full max-w-full scroll-mt-4 pb-9 sm:scroll-mt-10"
  >
    <div class="group/message grid w-full grid-cols-[2.25rem_calc(100%-3.75rem)] gap-6">
      <div class="sticky top-0 flex size-9 shrink-0 items-center justify-center rounded-full bg-red">
        <img
          :src="iconDocueAi"
          class="ml-px size-9 mix-blend-plus-lighter"
        >
      </div>

      <div class="col-start-2 flex flex-col gap-2">
        <div class="rounded-md bg-gray-100 p-2.5 text-sm">
          <ComarkRenderer
            v-if="tree"
            :tree
            class="flex flex-col gap-1.5"
            :components="componentMap"
          />
        </div>
      </div>
    </div>
  </article>
</template>
